import { GoogleMap } from '@react-google-maps/api'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import PropertyMarker from 'components/Common/PropertyMap/PropertyMarker'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineBuildingIcon from 'LuxKit/Icons/line/LineBuildingIcon'
import LineBusAltIcon from 'LuxKit/Icons/line/LineBusAltIcon'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Typography from 'LuxKit/Typography/Typography'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useCarHireLabels from 'hooks/CarHire/useCarHireLabels'
import { SHORT_TIME_FORMAT_AM_PM } from 'constants/dateFormats'
import { EnsureGoogleMapsLoaded } from 'contexts/GoogleMapsContext'
import useCarHireReservationInfo from 'hooks/CarHire/useCarHireReservationInfo'
import { fitMarkersOnMap } from 'lib/maps/googleMapUtils'
import moment from 'moment'
import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import styled from 'styled-components'
import CarHireLocationModalSkeleton from './CarHireLocationModalSkeleton'
import { sortBy } from 'lib/array/arrayUtils'
import { mod } from 'lib/maths/mathUtils'
import {
  DROP_OFF_LOCATION_TAB_ID,
  PICK_UP_LOCATION_TAB_ID,
  type CarHireLocationTabId,
} from 'components/CarHire/carHireLocationTabs'

const MapContainer = styled.div`
  height: ${rem(375)};

  ${mediaQueryUp.tablet} {
    height: ${rem(280)};
  }

  .location-map {
    height: ${rem(375)};

    ${mediaQueryUp.tablet} {
      height: ${rem(280)};
    }
  }
`

const Content = styled(VerticalSpacer)`
  padding-top: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(24)};
  }
`

const PickUpTypeSection = styled(Pane)`
  padding: ${rem(16)};
`

const pickupTypeIconMap = new Map<App.CarHirePickupType, React.ReactElement>([
  // oxlint-disable-next-line react/jsx-key
  ['office', <LineBuildingIcon />],
  // oxlint-disable-next-line react/jsx-key
  ['shuttle', <LinePlaneFlyIcon />],
  // oxlint-disable-next-line react/jsx-key
  ['terminal', <LineBusAltIcon />],
])

interface OperatingTime {
  dayStart: string
  hours: string
}

function mapOperatingTimeToLabel(
  operatingTime: App.CarHireLocationOperatingTime,
  closedLabel: string,
): OperatingTime {
  return {
    dayStart: operatingTime.day,
    hours: (!operatingTime.start && !operatingTime.end)
      ? closedLabel
      : `${moment(operatingTime.start, 'HH:mm').format(SHORT_TIME_FORMAT_AM_PM)} - ${moment(operatingTime.end, 'HH:mm').format(SHORT_TIME_FORMAT_AM_PM)}`,
  }
}

function getOpeningGroups(
  location: App.CarHireReservationLocation,
  closedLabel: string,
) {
  const operatingTimes = location.operatingHours.map((operatingTime) => (
    mapOperatingTimeToLabel(operatingTime, closedLabel)
  ))

  // 0 is usually sunday, -1 shifts 0 to begin monday (*our* first day)
  return sortBy(operatingTimes, time => mod((new Date(time.dayStart).getDay() - 1), 7), 'asc')
}

function getPickUpTypeIcon(locationType: App.CarHireReservationLocation['type']) {
  if (!locationType) {
    return undefined
  }
  return pickupTypeIconMap.get((locationType))
}

function getPickUpTypeLabel(locationType: App.CarHireReservationLocation['type'], labelMap: Map<App.CarHirePickupType, string>) {
  if (!locationType) {
    return null
  }
  return labelMap.get(locationType)
}

interface Props {
  openTab: CarHireLocationTabId
  orderItem: App.OrderCarHireItem
}

function CarHireItemSummaryLocationModal(props: Props) {
  const intl = useIntl()
  const { pickupTypeLabelMap } = useCarHireLabels()
  const { openTab, orderItem } = props
  const pickUpTabLabel = intl.formatMessage({
    id: 'Vrej93gQdG49k7mAGZey',
    defaultMessage: 'Pick-up',
    description: 'Tab label for pick-up location in car hire location modal',
  })
  const dropOffTabLabel = intl.formatMessage({
    id: 'Pl-8onaTJOzpbr1hN0Pd',
    defaultMessage: 'Drop-off',
    description: 'Tab label for drop-off location in car hire location modal',
  })
  const closedLabel = intl.formatMessage({
    id: '7bgAuTU4K0qNnS91aG5H',
    defaultMessage: 'closed',
    description: 'Label shown when car hire location is closed on a given day',
  })
  const tabOptions = useMemo(() => ([
    { id: PICK_UP_LOCATION_TAB_ID, label: pickUpTabLabel },
    { id: DROP_OFF_LOCATION_TAB_ID, label: dropOffTabLabel },
  ]), [pickUpTabLabel, dropOffTabLabel])
  const [activeTabId, setActiveTabId] = useState<CarHireLocationTabId>(openTab)
  const [map, setMap] = useState<google.maps.Map>()
  const isPickUpTabSelected = activeTabId === PICK_UP_LOCATION_TAB_ID

  const reservationId = orderItem.idReservation || ''

  const reservationInfo = useCarHireReservationInfo(reservationId).reservation

  const pickUpLocation = reservationInfo?.pickUp
  const dropOffLocation = reservationInfo?.dropOff
  const differentLocations = pickUpLocation?.id !== dropOffLocation?.id
  const title = differentLocations
    ? intl.formatMessage({
        id: 'LRu5-mZWfO-lZvKjWM1J',
        defaultMessage: 'Pick-up & Drop-off location',
        description: 'Modal title when car hire pick-up and drop-off are at different locations',
      })
    : intl.formatMessage({
        id: 'JhZ2KriBWExpI11exLMi',
        defaultMessage: 'Pick-up & Drop-off locations',
        description: 'Modal title when car hire pick-up and drop-off are at the same location',
      })

  // Fit the correct bounds on load
  const onLoad = useCallback((map: google.maps.Map) => { setMap(map) }, [])
  const markers = useMemo(() => {
    if (pickUpLocation?.latitude && dropOffLocation?.latitude && pickUpLocation?.longitude && dropOffLocation?.longitude) {
      const pickUp: App.MapMarker = {
        id: 'location',
        type: 'property',
        latitude: pickUpLocation.latitude,
        longitude: pickUpLocation.longitude,
      }
      const dropOff: App.MapMarker = differentLocations ? {
        id: 'location',
        type: 'property',
        latitude: dropOffLocation.latitude,
        longitude: dropOffLocation.longitude,
      } : pickUp
      return {
        pickUp,
        dropOff,
      }
    }
  }, [dropOffLocation, pickUpLocation, differentLocations])

  const openingGroups = useMemo(() => {
    const pickUp = pickUpLocation && getOpeningGroups(pickUpLocation, closedLabel)
    const dropOff = dropOffLocation && differentLocations
      ? getOpeningGroups(dropOffLocation, closedLabel)
      : pickUp
    return {
      pickUp,
      dropOff,
    }
  }, [pickUpLocation, differentLocations, dropOffLocation, closedLabel])

  useEffect(() => {
    if (map && markers) {
      // we need to wait for the open animation to complete
      const timeout = setTimeout(() => {
        fitMarkersOnMap(map, [isPickUpTabSelected ? markers.pickUp : markers.dropOff])
      }, 500)

      return () => clearTimeout(timeout)
    }
  }, [map, isPickUpTabSelected]) // oxlint-disable-line react-hooks/exhaustive-deps

  const hasContent = pickUpLocation && markers && openingGroups && dropOffLocation

  return <ModalBase>
    <ModalHeader title={title} />
    <ModalBody>
      {!hasContent && <CarHireLocationModalSkeleton />}
      {hasContent &&
        <>
          {differentLocations &&
            <TabButtonsGroup fit="grow">
              {tabOptions.map((option) => (
                <TabButton
                  isActive={option.id === activeTabId}
                  key={option.id}
                  size="medium"
                  onClick={() => setActiveTabId(option.id)}
                >
                  {option.label}
                </TabButton>
              ))}
            </TabButtonsGroup>}
          <MapContainer>
            <EnsureGoogleMapsLoaded loadingElement={<LoadingIndicator inline />}>
              <GoogleMap
                id="le-map"
                onLoad={onLoad}
                zoom={16}
                mapContainerClassName="location-map"
              >
                <PropertyMarker
                  marker={isPickUpTabSelected ? markers.pickUp : markers.dropOff}
                  title={isPickUpTabSelected ? pickUpLocation.address : dropOffLocation.address}
                />
              </GoogleMap>
            </EnsureGoogleMapsLoaded>
          </MapContainer>
          <ModalContent>
            <Content gap={24}>
              <div>
                <BodyText variant="large" weight="bold">{intl.formatMessage({
                  id: 'QgCrJ1umsYspnwAt9meW',
                  defaultMessage: 'Address',
                  description: 'Label for address section in car hire location modal',
                })}
                </BodyText>
                <BodyText variant="large">
                  {isPickUpTabSelected ? pickUpLocation?.locationName : dropOffLocation?.locationName}<br />
                  {isPickUpTabSelected ? pickUpLocation.address : dropOffLocation.address}
                </BodyText>
              </div>

              {Boolean(pickUpLocation.type) && Boolean(pickUpLocation.type) &&
                <PickUpTypeSection background="neutral-seven">
                  <BodyTextBlock
                    variant="medium"
                    startIcon={getPickUpTypeIcon(isPickUpTabSelected ? pickUpLocation.type : dropOffLocation.type)}
                  >
                    {getPickUpTypeLabel(isPickUpTabSelected ? pickUpLocation.type : dropOffLocation.type, pickupTypeLabelMap)}
                  </BodyTextBlock>
                </PickUpTypeSection>}

              <div>
                <BodyText variant="large" weight="bold">{intl.formatMessage({
                  id: 'BCoEeOD-ztdrAtu9LdNM',
                  defaultMessage: 'Opening hours',
                  description: 'Label for opening hours section in car hire location modal',
                })}
                </BodyText>
                {((isPickUpTabSelected ? openingGroups.pickUp : openingGroups.dropOff) || [])
                  .map(day => <BodyText key={day.dayStart} variant="large">
                    <FormattedMessage
                      id="srnk_mu7VUauu0w6l-5H"
                      defaultMessage="<dayName>{day}</dayName>: {hours}"
                      description="Operating hours for a specific day at car hire location"
                      values={{
                        dayName: (chunks) => <Typography format="titlecase" as="span">{chunks}</Typography>,
                        day: day.dayStart,
                        hours: day.hours,
                      }}
                    />
                  </BodyText>)}
              </div>
            </Content>
          </ModalContent>
        </>}
    </ModalBody>
  </ModalBase>
}

export default CarHireItemSummaryLocationModal
